/**
 * UnauthorizedModulePage — Phase 75
 *
 * Branded fallback view rendered when the currently active company is NOT
 * authorized for the requested module AND no auto-switch target exists.
 *
 * ─── Why a dedicated page (not a redirect) ──────────────────────────────────
 * Before Phase 75, an unlicensed-module attempt fell through the router's
 * catch-all (`<Route path="*" element={<Navigate to="/" replace />} />`) and
 * dumped the user onto the public marketing landing page — a jarring,
 * disorienting failure mode for a routine cross-module click.
 *
 * This page renders INSIDE the existing app shell (Navbar + minimal frame)
 * so the user keeps their session, language, and visual context.  It
 * surfaces three actionable affordances:
 *
 *   A. Quick company switcher — every company the user belongs to, with a
 *      lock icon on those that are also unlicensed for this module and a
 *      green check on those that ARE licensed.  Clicking a licensed entry
 *      switches the active company and navigates to the same module URL,
 *      which now resolves cleanly.
 *
 *   B. A "back to a working module" CTA that picks the highest-priority
 *      module the CURRENT company IS licensed for (cashflow → inventory →
 *      hr), defaulting to the workspaces page if none.
 *
 *   C. A subtle "back" link to /app for the user who just wants out.
 *
 * The Hebrew copy follows Eli's spec exactly:
 *   "החברה הנוכחית (Company Name) אינה מורשית לגשת למודל זה."
 *
 * ─── Self-contained ────────────────────────────────────────────────────────
 * No router-level dependencies beyond `useNavigate`.  Pure presentation +
 * one company-switch side-effect.  This means it can be embedded by
 * ModuleGuard OR rendered directly as a route (e.g. /app/unauthorized).
 */

import { useMemo, useState }       from 'react';
import { useNavigate }             from 'react-router-dom';
import { ShieldAlert, CircleCheck as CheckCircle2, Lock, Building2, ChevronDown, ArrowRight, ChartLine as LineChart, Package, Users, FileScan, Cable, LayoutGrid } from 'lucide-react';
import { useCompany }              from '@/context/CompanyContext';
import { usePhase76Auth }          from '@/lib/auth';
import { type AppModule }          from '@/lib/rbac';

// ─── Module → metadata ────────────────────────────────────────────────────
const MODULE_META: Record<AppModule, { label: string; path: string; icon: React.ReactNode }> = {
  cashflow: {
    label: 'תזרים מזומנים',
    path:  '/app/cashflow',
    icon:  <LineChart className="w-4 h-4" />,
  },
  inventory: {
    label: 'ניהול מלאי',
    path:  '/app/inventory',
    icon:  <Package className="w-4 h-4" />,
  },
  hr: {
    label: 'ניהול עובדים',
    path:  '/app/hr',
    icon:  <Users className="w-4 h-4" />,
  },
  invoice_intake: {
    label: 'קליטת חשבוניות',
    path:  '/app/invoice-intake',
    icon:  <FileScan className="w-4 h-4" />,
  },
  interfaces: {
    label: 'ממשקים',
    path:  '/app/interfaces',
    icon:  <Cable className="w-4 h-4" />,
  },
};

// Priority order for the "back to a working module" CTA.  Cashflow first
// because it is the most commonly-licensed and lightest-weight module.
const MODULE_PRIORITY: AppModule[] = ['cashflow', 'inventory', 'hr'];

interface UnauthorizedModulePageProps {
  /** The module the user tried to access. */
  module:            AppModule;
  /** Legal name of the currently active company (for the headline message). */
  activeCompanyName: string | null;
}

export function UnauthorizedModulePage({
  module,
  activeCompanyName,
}: UnauthorizedModulePageProps) {
  const navigate = useNavigate();

  // Phase 76.2.E: cross-company module availability now flows exclusively
  // through the Phase 76 context surface (`hasModuleInCompany` reads from
  // the cached `subscribedModulesByCompany` map, itself sourced from
  // `get_my_companies_with_modules()` at hydration time). The residual
  // dependency on the legacy CompanyContext is reduced to its
  // `setActiveCompanyId` writer — which we mirror via the Phase 76 setter
  // below for full evolutionary parity.
  const {
    companies:          phase76Companies,
    activeCompanyId,
    hasModuleInCompany,
    setActiveCompanyId: setPhase76ActiveCompanyId,
  } = usePhase76Auth();

  const { setActiveCompanyId: setLegacyActiveCompanyId } = useCompany();

  const [pickerOpen, setPickerOpen] = useState(false);

  const moduleMeta = MODULE_META[module];

  // ── Companies catalog ───────────────────────────────────────────────────
  // We split into "licensed for THIS module" vs "not licensed" so the picker
  // can sort by relevance and tag each entry.
  const licensedCompanies = useMemo(
    () => phase76Companies.filter(
      c => c.company_id !== activeCompanyId
        && hasModuleInCompany(c.company_id, module),
    ),
    [phase76Companies, activeCompanyId, module, hasModuleInCompany],
  );

  const unlicensedOtherCompanies = useMemo(
    () => phase76Companies.filter(
      c => c.company_id !== activeCompanyId
        && !hasModuleInCompany(c.company_id, module),
    ),
    [phase76Companies, activeCompanyId, module, hasModuleInCompany],
  );

  // ── "Working module" fallback for the current company ──────────────────
  const fallbackModule: AppModule | null = useMemo(() => {
    if (!activeCompanyId) return null;
    for (const m of MODULE_PRIORITY) {
      if (m === module) continue;
      if (hasModuleInCompany(activeCompanyId, m)) return m;
    }
    return null;
  }, [activeCompanyId, module, hasModuleInCompany]);

  // ── Actions ────────────────────────────────────────────────────────────
  const switchToCompany = (companyId: string) => {
    // Write through to BOTH contexts during the evolutionary cutover — they
    // share the same localStorage key but each holds its own in-memory state.
    setPhase76ActiveCompanyId(companyId);
    setLegacyActiveCompanyId(companyId);
    setPickerOpen(false);
    // Re-enter the same module URL — ModuleGuard will pass this time.
    navigate(moduleMeta.path, { replace: true });
  };

  const goToFallbackModule = () => {
    if (fallbackModule) {
      navigate(MODULE_META[fallbackModule].path, { replace: true });
    } else {
      navigate('/app', { replace: true });
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <div dir="rtl" className="flex items-start justify-center px-4 py-10 sm:py-16">
      <div className="w-full max-w-2xl bg-white border border-gray-200 rounded-3xl shadow-sm overflow-hidden">

        {/* Banner */}
        <div className="flex items-center gap-4 px-6 sm:px-8 py-5 border-b border-gray-100 bg-gradient-to-l from-amber-50 to-white">
          <div className="shrink-0 w-12 h-12 rounded-2xl bg-amber-100 flex items-center justify-center">
            <ShieldAlert className="w-6 h-6 text-amber-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-lg font-bold text-gray-900">
              גישה למודל אינה מורשית עבור החברה הפעילה
            </h1>
            <p className="text-xs sm:text-sm text-gray-500 mt-0.5 flex items-center gap-1.5">
              {moduleMeta.icon}
              <span>{moduleMeta.label}</span>
            </p>
          </div>
        </div>

        {/* Message — exact Hebrew copy from the Phase 75 spec */}
        <div className="px-6 sm:px-8 py-6">
          <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
            החברה הנוכחית{' '}
            <span className="font-semibold text-gray-900">
              ({activeCompanyName?.trim() ? activeCompanyName : 'ללא שם'})
            </span>{' '}
            אינה מורשית לגשת למודל זה.
          </p>
          <p className="text-xs text-gray-500 mt-2 leading-relaxed">
            החברה / המשתמש אינם רשומים למודל זה. אנא דבר עם מנהל החברה להשלמת ההרשמה.
            לחלופין, ניתן לעבור לחברה אחרת בחשבונך שכן רשומה למודל.
          </p>
        </div>

        {/* Actions */}
        <div className="px-6 sm:px-8 pb-6 space-y-4">

          {/* A. Quick company switcher */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setPickerOpen(o => !o)}
              disabled={phase76Companies.length <= 1}
              className={[
                'w-full flex items-center justify-between gap-3 px-4 py-3 rounded-2xl border text-sm font-semibold transition-colors',
                phase76Companies.length <= 1
                  ? 'bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed'
                  : pickerOpen
                    ? 'bg-primary-50 border-primary-300 text-primary-700'
                    : 'bg-white border-gray-200 text-gray-800 hover:border-primary-300',
              ].join(' ')}
              aria-expanded={pickerOpen}
              aria-haspopup="listbox"
            >
              <span className="flex items-center gap-2 min-w-0">
                <Building2 className="w-4 h-4 shrink-0" />
                <span className="truncate">
                  {licensedCompanies.length > 0
                    ? `החלף לחברה מורשית (${licensedCompanies.length})`
                    : 'בחר חברה אחרת מהחשבון'}
                </span>
              </span>
              <ChevronDown
                className={[
                  'w-4 h-4 shrink-0 transition-transform',
                  pickerOpen ? 'rotate-180' : '',
                ].join(' ')}
              />
            </button>

            {pickerOpen && phase76Companies.length > 1 && (
              <div
                role="listbox"
                className="absolute z-20 top-full mt-2 inset-x-0 bg-white border border-gray-200 rounded-2xl shadow-xl overflow-hidden"
              >
                {licensedCompanies.length > 0 && (
                  <div className="px-3 pt-3 pb-1.5 border-b border-gray-100">
                    <p className="text-[10px] font-semibold text-emerald-600 uppercase tracking-wide">
                      חברות מורשות למודל {moduleMeta.label}
                    </p>
                  </div>
                )}

                <div className="max-h-72 overflow-y-auto py-1">
                  {licensedCompanies.map(c => (
                    <button
                      key={c.company_id}
                      role="option"
                      aria-selected={false}
                      onClick={() => switchToCompany(c.company_id)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-right hover:bg-emerald-50 transition-colors"
                    >
                      <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-500" />
                      <span className="flex-1 min-w-0 truncate text-gray-800">
                        {c.legal_name || '(חברה ללא שם)'}
                      </span>
                      <ArrowRight className="w-3.5 h-3.5 shrink-0 text-emerald-500 rotate-180" />
                    </button>
                  ))}

                  {unlicensedOtherCompanies.length > 0 && (
                    <>
                      <div className="px-3 pt-3 pb-1.5 border-t border-gray-100">
                        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                          לא מורשות למודל זה
                        </p>
                      </div>
                      {unlicensedOtherCompanies.map(c => (
                        <button
                          key={c.company_id}
                          role="option"
                          aria-selected={false}
                          disabled
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-right text-gray-400 cursor-not-allowed"
                          title="חברה זו לא רשומה למודל המבוקש"
                        >
                          <Lock className="w-4 h-4 shrink-0 text-gray-300" />
                          <span className="flex-1 min-w-0 truncate">
                            {c.legal_name || '(חברה ללא שם)'}
                          </span>
                        </button>
                      ))}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* B. Back to a working module */}
          <button
            type="button"
            onClick={goToFallbackModule}
            className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-2xl bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold transition-colors"
          >
            <span className="flex items-center gap-2 min-w-0">
              {fallbackModule ? MODULE_META[fallbackModule].icon : <LayoutGrid className="w-4 h-4" />}
              <span className="truncate">
                {fallbackModule
                  ? `חזרה למודל פעיל: ${MODULE_META[fallbackModule].label}`
                  : 'חזרה ללוח הראשי'}
              </span>
            </span>
            <ArrowRight className="w-4 h-4 shrink-0 rotate-180" />
          </button>

          {/* C. Quiet "back to app" */}
          <button
            type="button"
            onClick={() => navigate('/app', { replace: true })}
            className="w-full text-xs text-gray-500 hover:text-gray-700 transition-colors"
          >
            או חזור לדף הבית של המערכת
          </button>
        </div>
      </div>
    </div>
  );
}
